import moment from 'moment-timezone';
import {
  DoctorRoleGroupId,
  NurseRoleGroupId,
  SocialWorkerRoleGroupId,
  SpiritualRoleGroupId,
  AideRoleGroupId,
  VolunteerRoleGroupId,
} from '@mummssoftware/utils/roleData';
import {
  computeCompliancePeriods,
  CompliancePeriod,
} from '../../calendar/data/computeCalendarViewData';
import { createSiteCalendarClock } from '../../calendar/data/siteCalendarClock';
import {
  MissedPeriod,
  PatientComplianceRow,
  ProviderMissed,
  SiteComplianceInput,
  SiteComplianceResult,
} from './types';

const ISO_DATE = 'YYYY-MM-DD';
/** The calendar's non-padded date key. */
const DATE_KEY = 'YYYY-M-D';

/** Longest selectable range, in days (six weeks, both ends inclusive). */
export const MAX_RANGE_DAYS = 42;

/**
 * Longest administration period (bi-monthly). A period that ends in the range
 * may have started this long before it, and its visits must be fetched too.
 */
export const VISIT_LOOKBACK_DAYS = 62;

export const addDaysIso = (isoDate: string, days: number): string =>
  moment(isoDate, ISO_DATE).add(days, 'days').format(ISO_DATE);

/** Inclusive day count of a range; 0 when it is inverted. */
export const rangeDayCount = (fromDate: string, toDate: string): number =>
  Math.max(
    0,
    moment(toDate, ISO_DATE).diff(moment(fromDate, ISO_DATE), 'days') + 1,
  );

export type ComplianceRange = { fromDate: string; toDate: string };

/**
 * Keeps a range valid after one end moved: the other end follows so the
 * range is never inverted and never longer than MAX_RANGE_DAYS.
 */
export const clampComplianceRange = (
  fromDate: string,
  toDate: string,
  moved: 'from' | 'to',
): ComplianceRange => {
  let from = fromDate;
  let to = toDate;
  if (moved === 'from') {
    if (to < from) to = from;
    if (rangeDayCount(from, to) > MAX_RANGE_DAYS) {
      to = addDaysIso(from, MAX_RANGE_DAYS - 1);
    }
  } else {
    if (from > to) from = to;
    if (rangeDayCount(from, to) > MAX_RANGE_DAYS) {
      from = addDaysIso(to, -(MAX_RANGE_DAYS - 1));
    }
  }
  return { fromDate: from, toDate: to };
};

/**
 * Plans and visits to fetch for a range: visits from VISIT_LOOKBACK_DAYS
 * before it, so a period that ends in the range but started before it is
 * judged on all of its visits.
 */
export const complianceFetchRange = (
  fromDate: string,
  toDate: string,
): ComplianceRange => ({
  fromDate: addDaysIso(fromDate, -VISIT_LOOKBACK_DAYS),
  toDate,
});

const dateKeyToIso = (dateKey: string): string =>
  moment(dateKey, DATE_KEY).format(ISO_DATE);

const groupBy = <T>(
  items: T[],
  key: (item: T) => string | undefined,
): Map<string, T[]> => {
  const map = new Map<string, T[]>();
  items.forEach((item) => {
    const k = key(item);
    if (k === undefined) return;
    const arr = map.get(k) ?? [];
    arr.push(item);
    map.set(k, arr);
  });
  return map;
};

/** CPVF display order; any other role group follows alphabetically. */
const PROVIDER_ORDER: number[] = [
  DoctorRoleGroupId,
  NurseRoleGroupId,
  SocialWorkerRoleGroupId,
  SpiritualRoleGroupId,
  AideRoleGroupId,
  VolunteerRoleGroupId,
];

const compareProviders = (a: ProviderMissed, b: ProviderMissed): number => {
  const ia = PROVIDER_ORDER.indexOf(a.roleGroupId);
  const ib = PROVIDER_ORDER.indexOf(b.roleGroupId);
  if (ia !== -1 || ib !== -1) {
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  }
  return a.roleGroupName.localeCompare(b.roleGroupName);
};

/** Groups a patient's missed periods (already sorted) by provider with totals. */
export const groupPeriodsByProvider = (
  periods: MissedPeriod[],
): ProviderMissed[] => {
  const byRole = new Map<number, ProviderMissed>();
  periods.forEach((period) => {
    const provider = byRole.get(period.roleGroupId) ?? {
      roleGroupId: period.roleGroupId,
      roleGroupName: period.roleGroupName,
      planned: 0,
      completed: 0,
      inProgress: 0,
      missed: 0,
      periods: [],
    };
    provider.planned += period.planned;
    provider.completed += period.completed;
    provider.inProgress += period.inProgress;
    provider.missed += period.missed;
    provider.periods.push(period);
    byRole.set(period.roleGroupId, provider);
  });
  return Array.from(byRole.values()).sort(compareProviders);
};

const displayName = (lastName: string, firstName: string): string =>
  [lastName, firstName].filter((s) => s && s.trim()).join(', ');

const compareNames = (
  a: PatientComplianceRow,
  b: PatientComplianceRow,
): number =>
  a.lastName.localeCompare(b.lastName, undefined, { sensitivity: 'base' }) ||
  a.firstName.localeCompare(b.firstName, undefined, { sensitivity: 'base' }) ||
  a.patientNumber.localeCompare(b.patientNumber);

const comparePeriods = (a: MissedPeriod, b: MissedPeriod): number =>
  a.periodEnd.localeCompare(b.periodEnd) ||
  a.periodStart.localeCompare(b.periodStart) ||
  a.roleGroupName.localeCompare(b.roleGroupName) ||
  a.cpfId - b.cpfId;

/**
 * Lists, per active patient of the site, the administration periods that
 * ended inside the selected range with fewer visits than planned.
 *
 * Periods come from the same generator as the patient Compliance View
 * (`computeCompliancePeriods`), so a weekly plan is judged week by week, a
 * monthly plan month by month, and "missed" means exactly what the
 * calendar's red box means. A period is counted when
 *   - it is not superseded by a later frequency (interrupted),
 *   - its last day is before today in the site timezone (it has ended), and
 *   - its last day falls inside [fromDate, toDate].
 * The range is a filter on ended periods, never a projection: a period that
 * is still open shows nothing, and each period belongs to exactly one range
 * (the one containing its last day), so no visit is counted twice across
 * consecutive ranges.
 */
export const computeSiteCompliance = (
  input: SiteComplianceInput,
): SiteComplianceResult => {
  const {
    fromDate,
    toDate,
    siteId,
    patients: allPatients,
    carePlans,
    visits,
    roleGroups,
    startOfWeek,
    siteTimezone,
  } = input;

  const patients =
    siteId == null || siteId === ''
      ? allPatients
      : allPatients.filter((p) => p.siteId === Number(siteId));

  const clock = createSiteCalendarClock(siteTimezone);
  const todayIso = dateKeyToIso(clock.dateKey(clock.todayStart()));
  const yesterdayIso = addDaysIso(todayIso, -1);
  const countedThrough = toDate < yesterdayIso ? toDate : yesterdayIso;

  if (countedThrough < fromDate) {
    return {
      rows: [],
      patientsConsidered: patients.length,
      countedThrough: null,
    };
  }

  // Visible range for the generator: [fromDate, countedThrough] (end
  // exclusive). It returns every period overlapping it, unclipped, so the
  // last-day filter below sees full periods.
  const calendarDates: [string, string] = [
    fromDate,
    addDaysIso(countedThrough, 1),
  ];

  const roleGroupNames = new Map(
    roleGroups.map((rg) => [Number(rg.id), rg.name]),
  );
  const plansByPatient = groupBy(carePlans, (p) => p.patientNumber);
  const visitsByPatient = groupBy(visits, (v) => v.patientnumber);

  const rows: PatientComplianceRow[] = [];

  patients.forEach((patient) => {
    const patientPlans = plansByPatient.get(patient.patientNumber) ?? [];
    if (!patientPlans.length) return;
    const patientVisits = visitsByPatient.get(patient.patientNumber) ?? [];

    const periods: CompliancePeriod[] = computeCompliancePeriods({
      calendarDates,
      carePlanFrequencies: patientPlans,
      visits: patientVisits,
      roleGroups,
      startOfWeek,
      siteTimezone,
    });

    const missedPeriods: MissedPeriod[] = [];
    periods.forEach((period) => {
      if (period.interrupted || !period.isPast || period.missed <= 0) return;
      const periodEnd = dateKeyToIso(period.periodEnd);
      if (periodEnd < fromDate || periodEnd > countedThrough) return;
      missedPeriods.push({
        cpfId: period.cpfId,
        roleGroupId: period.roleGroupId,
        roleGroupName:
          roleGroupNames.get(period.roleGroupId) ??
          period.discipline ??
          String(period.roleGroupId),
        frequencyLabel: period.label,
        periodStart: dateKeyToIso(period.periodStart),
        periodEnd,
        planned: period.frequency,
        completed: period.completed - period.inProgress,
        inProgress: period.inProgress,
        missed: period.missed,
      });
    });

    if (!missedPeriods.length) return;
    missedPeriods.sort(comparePeriods);

    rows.push({
      patientNumber: patient.patientNumber,
      patientId: patient.patientId,
      siteId: patient.siteId,
      lastName: patient.lastName,
      firstName: patient.firstName,
      displayName: displayName(patient.lastName, patient.firstName),
      missedPeriods,
      providers: groupPeriodsByProvider(missedPeriods),
      missedTotal: missedPeriods.reduce((sum, p) => sum + p.missed, 0),
    });
  });

  rows.sort(compareNames);

  return { rows, patientsConsidered: patients.length, countedThrough };
};

/**
 * True when rows fetched for `fetched` also serve `needed`: getsitecareplans
 * and getsitevisits are plain range filters, so a window that contains the
 * needed one holds every row it would return.
 */
export const fetchWindowCovers = (
  fetched: ComplianceRange | null | undefined,
  needed: ComplianceRange,
): boolean =>
  !!fetched &&
  fetched.fromDate <= needed.fromDate &&
  fetched.toDate >= needed.toDate;
